import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  IManagedDbBatchRequest,
  IManagedDbBatchResponse,
  IManagedDbBatchResult,
  IManagedDbRow,
  TableEditorService
} from "../../../services/table-editor-service";
import { buildBatchRequestFromPending } from "../batch-request-builder";
import { buildLocalMutationValidationIssues, detectRefreshConflicts, ICellValidationIssue } from "../cell-validation";
import {
  IColumnValueMap,
  IPendingChanges,
  IPendingRowInsert,
  isDirtyChanges,
  isPersistableInsert,
  PendingChangesAction,
  pendingChangesMutationCount,
  rowPkStringFromRow,
  RowPkString
} from "../pending-changes-reducer";
import { emptyHistoryState, pendingHistoryReducer } from "../pending-history";
import {
  buildTableEditorErrorAlert,
  buildTableEditorSaveFailureAlert,
  createTableEditorAlert,
  ITableEditorAlert
} from "../table-editor-errors";

// KOA-5857 — Pending edits + save orchestration hook.
//
// Owns the pure-reducer pending state, every dispatcher the grid/context
// menu need, save lifecycle (atomic toggle, error formatting, saved badge),
// and the keyboard shortcut binding (⌘S / Esc). Pulled out so the parent
// is a composition root and so the save flow has one place to read.
//
// Does NOT own:
//   * columns / rows — passed in; the hook reads `columns` to strip
//     auto-generated columns on clone, and uses `primaryKeyColumns` to
//     fail fast on PK-less tables before any SQL is built.
//   * refreshRows — passed in; called after save so the grid reloads.

export interface IUseTableEditorMutationsProps {
  tableName: string;
  tableSchema?: string;
  columns: IDatabaseColumnModel[];
  primaryKeyColumns: string[];
  // Rows from the latest server fetch. The hook reads this for
  // refresh-time conflict detection: if a pending update's captured
  // `originals` no longer match the server's current values, we
  // surface the divergence as a per-row warning BEFORE the user
  // clicks Save and discovers it via a conflict response.
  rows: IManagedDbRow[];
  refreshRows: () => Promise<void>;
  // Whether ⌘S / ⌘Z / ⌘Y act on this editor. Defaults to true. The table
  // editor keeps its Data tab mounted while the user is on Columns, and
  // the listener is on `window`: a hidden grid must not save or undo rows
  // behind the tab the user is looking at.
  shortcutsEnabled?: boolean;
}

export interface IUseTableEditorMutationsResult {
  pending: IPendingChanges;
  isDirty: boolean;
  dirtyCount: number;
  isSaving: boolean;
  saveError: ITableEditorAlert | null;
  saveIssuesByClientId: Map<string, string>;
  savedAt: number | null;
  atomicSave: boolean;
  setAtomicSave: (next: boolean) => void;
  setSaveError: (error: ITableEditorAlert | null) => void;
  editCell: (pk: RowPkString, column: string, value: unknown, originalValue: unknown) => void;
  editInsertCell: (clientId: string, column: string, value: unknown) => void;
  addInsertRow: () => void;
  cloneRow: (source: IManagedDbRow) => void;
  // Variant used by AI-generate / import paths that already have a
  // ready-made values map. Returns the freshly minted clientId so the
  // caller can correlate (e.g. for the AI highlight pulse).
  addRowFromValues: (values: IColumnValueMap) => string;
  removeInsertRow: (clientId: string) => void;
  markRowDeleted: (pk: RowPkString) => void;
  unmarkRowDeleted: (pk: RowPkString) => void;
  discardAll: () => void;
  // Resolves true when nothing is left to save — the edits were saved, or
  // there were none. False when the save did not go through: one already
  // in flight, no primary key, local validation failed, rows the server
  // refused, or a request that threw. The alert says why; the caller only
  // decides whether to go on (the table editor's "Save & close").
  saveAll: () => Promise<boolean>;
  // Returns true when the pending edits pass local validation (no
  // missing required cells, no malformed GUIDs). On false, the hook has
  // already raised a `saveError` alert and populated per-row highlights
  // so the caller just needs to bail. Used by Save and Preview SQL so
  // both gate the wire payload through the same validation pass.
  validateLocalEdits: () => boolean;
  // Total local validation issues in the current pending set. Consumers
  // can use this to hide actions that wouldn't help while issues remain
  // (e.g. "Retry save" — re-sending the same invalid batch repeats the
  // same failure).
  localValidationIssueCount: number;
  // pk → human-readable conflict message for pending updates whose
  // captured `originals` no longer match the server's current row
  // values. Derived from `rows` + `pending` each render; the editor
  // merges with `saveIssuesByClientId` (server-returned + local
  // validation) when feeding the grid so all three signals can share
  // the per-row highlight + tooltip surface.
  refreshConflictsByPk: Map<string, string>;
  clearResolved: (clientIds: string[]) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  endEditSession: () => void;
}

// Re-export the wire-format builder so existing callers (SqlPreviewModal,
// table-rows-editor, and the new mutations test) can keep importing it
// from this hook module without breaking. The implementation lives in
// `batch-request-builder.ts` so it's import-safe in environments where
// the `TableEditorService` chain can't be loaded (e.g. unit tests).
export { buildBatchRequestFromPending } from "../batch-request-builder";

// Pending-insert tmp id. Prefixed `tmp-` so a glance at any log /
// server payload makes the lifecycle obvious; crypto.randomUUID for
// collision resistance — every browser the designer ships to has it.
const generateClientId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `tmp-${crypto.randomUUID()}`;
  }
  // Defensive fallback for legacy / non-browser test environments.
  const random: string = Math.random().toString(36).slice(2, 10);
  const time: string = Date.now().toString(36);
  return `tmp-${time}-${random}`;
};

const saveIssuesFromBatchResponse = (response: IManagedDbBatchResponse): Map<string, string> => {
  const issues: Map<string, string> = new Map<string, string>();
  const results: IManagedDbBatchResult[] = response.results ?? [];
  results.forEach((result: IManagedDbBatchResult): void => {
    if (result.success || result.clientId === undefined) {
      return;
    }
    issues.set(result.clientId, result.error ?? "This mutation failed.");
  });
  return issues;
};

const MAX_LOCAL_VALIDATION_DETAILS = 12;

// Inserts are keyed by a synthetic `tmp-<uuid>` so dispatch can address
// them before they have a server-side pk. The tmp id is internal — surfacing
// it in a user-facing error list reads as gibberish. Reduce every persistable
// insert to a 1-based position so the alert says "New row 2" instead.
const insertIndexByClientId = (pending: IPendingChanges): Map<string, number> => {
  const result: Map<string, number> = new Map<string, number>();
  pending.inserts.filter(isPersistableInsert).forEach((insert: IPendingRowInsert, index: number): void => {
    result.set(insert.clientId, index);
  });
  return result;
};

const rowLabelForIssue = (clientId: string, insertIndices: Map<string, number>): string => {
  const insertIndex: number | undefined = insertIndices.get(clientId);
  if (insertIndex !== undefined) {
    return `New row ${insertIndex + 1}`;
  }
  // For updates / deletes the clientId IS the primary-key string the user
  // typed (e.g. "Id=42" or "OrgId=1, Id=2") — already meaningful, leave alone.
  return clientId;
};

const localIssueDetail = (issue: ICellValidationIssue, insertIndices: Map<string, number>): string => {
  return `${rowLabelForIssue(issue.clientId, insertIndices)} / ${issue.column}: ${issue.message}`;
};

const saveIssuesFromLocalValidation = (issues: ICellValidationIssue[]): Map<string, string> => {
  const byClientId: Map<string, string> = new Map<string, string>();
  issues.forEach((issue: ICellValidationIssue): void => {
    const text: string = `${issue.column}: ${issue.message}`;
    const existing: string | undefined = byClientId.get(issue.clientId);
    byClientId.set(issue.clientId, existing === undefined ? text : `${existing}; ${text}`);
  });
  return byClientId;
};

export const useTableEditorMutations = (props: IUseTableEditorMutationsProps): IUseTableEditorMutationsResult => {
  const { tableName, tableSchema, columns, primaryKeyColumns, rows, refreshRows, shortcutsEnabled = true } = props;

  // History-aware reducer wraps the existing pendingChangesReducer with
  // a JSON-Patch undo / redo stack. The wrapping reducer dispatches the
  // underlying action via `{ type: "apply", action }`; we keep the public
  // `dispatch` signature so the rest of this file doesn't have to know.
  const [historyState, historyDispatch] = useReducer(pendingHistoryReducer, undefined, emptyHistoryState);
  const pending: IPendingChanges = historyState.state;
  const dispatch = useCallback((action: PendingChangesAction): void => {
    historyDispatch({ type: "apply", action });
  }, []);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Synchronous mirror of `isSaving` so re-entrant `saveAll` calls (⌘S
  // racing a click, double-click, AI/import callbacks calling save while
  // a prior save is in flight) can early-return before React commits the
  // `setIsSaving(true)` from the first call. Without this, two identical
  // batches reach the server, both succeed, and inserts duplicate.
  const inFlightRef = useRef<boolean>(false);
  const [saveError, setSaveError] = useState<ITableEditorAlert | null>(null);
  const [saveIssuesByClientId, setSaveIssuesByClientId] = useState<Map<string, string>>(new Map<string, string>());
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Atomic-save mode: when true the server wraps the batch in a single
  // transaction (all-or-nothing). Default false to preserve the existing
  // best-effort-per-row UX. Saved as user-session state, not a setting —
  // the user picks per save batch.
  const [atomicSave, setAtomicSave] = useState<boolean>(false);

  const clearSaveIssue = useCallback((clientId: string): void => {
    setSaveIssuesByClientId((current: Map<string, string>): Map<string, string> => {
      if (!current.has(clientId)) {
        return current;
      }
      const next: Map<string, string> = new Map<string, string>(current);
      next.delete(clientId);
      return next;
    });
  }, []);

  const clearSaveIssues = useCallback((clientIds: string[]): void => {
    if (clientIds.length === 0) {
      return;
    }
    setSaveIssuesByClientId((current: Map<string, string>): Map<string, string> => {
      let changed: boolean = false;
      const next: Map<string, string> = new Map<string, string>(current);
      clientIds.forEach((clientId: string): void => {
        if (next.delete(clientId)) {
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, []);

  useEffect((): (() => void) | undefined => {
    if (savedAt === null) {
      return undefined;
    }
    const t = window.setTimeout((): void => setSavedAt(null), 2000);
    return (): void => window.clearTimeout(t);
  }, [savedAt]);

  // `dispatch` (useCallback over `historyDispatch`) and `historyDispatch`
  // are both stable across renders, so they're intentionally omitted
  // from the dep arrays below — keeping them would just add noise.
  const editCell = useCallback(
    (pk: RowPkString, column: string, value: unknown, originalValue: unknown): void => {
      clearSaveIssue(pk);
      dispatch({ type: "edit-cell", pk, column, value, originalValue });
    },
    [clearSaveIssue]
  );
  const editInsertCell = useCallback(
    (clientId: string, column: string, value: unknown): void => {
      clearSaveIssue(clientId);
      dispatch({ type: "edit-insert-cell", clientId, column, value });
    },
    [clearSaveIssue]
  );
  const addInsertRow = useCallback((): void => {
    dispatch({ type: "add-row", clientId: generateClientId() });
  }, []);
  // Clone: copy a source row's editable values into a fresh insert. Strip
  // identity / autoincrement columns so the DB assigns fresh ones. Keep PK
  // columns when they're not identity (composite PKs may be partly manual);
  // the user can adjust the PK before saving.
  const cloneRow = useCallback(
    (source: IManagedDbRow): void => {
      const values: IColumnValueMap = {};
      columns.forEach((c: IDatabaseColumnModel): void => {
        if (c.isIdentity || c.isAutoIncrement) {
          return;
        }
        values[c.name] = source[c.name];
      });
      dispatch({ type: "add-row", clientId: generateClientId(), values });
    },
    [columns]
  );
  const addRowFromValues = useCallback((values: IColumnValueMap): string => {
    const cid: string = generateClientId();
    dispatch({ type: "add-row", clientId: cid, values });
    return cid;
  }, []);
  const removeInsertRow = useCallback(
    (clientId: string): void => {
      clearSaveIssue(clientId);
      dispatch({ type: "remove-insert", clientId });
    },
    [clearSaveIssue]
  );
  const markRowDeleted = useCallback(
    (pk: RowPkString): void => {
      clearSaveIssue(pk);
      dispatch({ type: "delete-row", pk });
    },
    [clearSaveIssue]
  );
  const unmarkRowDeleted = useCallback(
    (pk: RowPkString): void => {
      clearSaveIssue(pk);
      dispatch({ type: "undelete-row", pk });
    },
    [clearSaveIssue]
  );
  const discardAll = useCallback((): void => {
    dispatch({ type: "reset" });
    // Keep history. The parent toolbar's discard button gates on
    // DISCARD_CONFIRM_THRESHOLD — small (1-2) discards skip the
    // confirm precisely BECAUSE ⌘Z is supposed to bring them back.
    // The previous behavior wiped history here, silently breaking
    // that promise: a user would discard 2 edits, hit ⌘Z, and
    // nothing would happen — straight data loss. The post-save
    // clear (saveAll) still wipes history because committed
    // mutations can't be undone safely; discard is a session-local
    // rollback and stays undoable.
    setSaveError(null);
    setSaveIssuesByClientId(new Map<string, string>());
  }, []);

  // Defense in depth against a hard data-loss bug: every call site that
  // renders this hook today re-keys on tableSchema/tableName, so the hook
  // remounts and `pending` starts empty when the user navigates to a
  // different table. If a future caller forgets to key, the hook would
  // otherwise carry pending edits across the switch and the next save
  // would ship table-A's mutations against table-B. This effect catches
  // that: when the identity actually changes after the first render, we
  // discard whatever was pending for the old table.
  const lastTableIdentityRef = useRef<string | null>(null);
  useEffect((): void => {
    const identity: string = `${tableSchema ?? ""}\x1F${tableName}`;
    const prior: string | null = lastTableIdentityRef.current;
    lastTableIdentityRef.current = identity;
    if (prior === null || prior === identity) {
      return;
    }
    discardAll();
  }, [tableName, tableSchema, discardAll]);
  const undo = useCallback((): void => historyDispatch({ type: "undo" }), []);
  const redo = useCallback((): void => historyDispatch({ type: "redo" }), []);
  // Save-issue highlights are keyed by clientId / pk. When a history
  // move (undo / redo) or a manual edit erases a row from `pending`,
  // the highlight for that row no longer applies and should drop on
  // its own. Issues for rows still present stay so the user doesn't
  // lose context from a server-side failure they haven't yet resolved.
  // Runs reactively on every pending change so we catch every
  // pathway without having to spray manual clears into each callback.
  useEffect((): void => {
    setSaveIssuesByClientId((current: Map<string, string>): Map<string, string> => {
      if (current.size === 0) {
        return current;
      }
      const stillPresent: Set<string> = new Set<string>();
      pending.updates.forEach((_value, pk: RowPkString): void => {
        stillPresent.add(pk);
      });
      pending.inserts.forEach((insert): void => {
        stillPresent.add(insert.clientId);
      });
      pending.deletes.forEach((pk: RowPkString): void => {
        stillPresent.add(pk);
      });
      let changed: boolean = false;
      const next: Map<string, string> = new Map<string, string>();
      current.forEach((message: string, clientId: string): void => {
        if (stillPresent.has(clientId)) {
          next.set(clientId, message);
        } else {
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [pending]);
  // Refresh-time conflict warnings. When the rows array updates (manual
  // refresh, post-save reload, page change), walk pending updates and
  // detect any whose captured `originals` no longer match the server's
  // current values. The user sees the row highlighted with a tooltip
  // BEFORE attempting a save that would otherwise come back with an
  // opaque conflict response. Keyed by pk so the grid's per-row
  // visual-state layer can merge them with the existing server-side
  // issue map.
  const rowsByPk: Map<RowPkString, IColumnValueMap> = useMemo((): Map<RowPkString, IColumnValueMap> => {
    const result: Map<RowPkString, IColumnValueMap> = new Map<RowPkString, IColumnValueMap>();
    if (primaryKeyColumns.length === 0) {
      return result;
    }
    rows.forEach((row: IManagedDbRow): void => {
      result.set(rowPkStringFromRow(row, primaryKeyColumns), row);
    });
    return result;
  }, [rows, primaryKeyColumns]);

  const refreshConflictsByPk: Map<string, string> = useMemo((): Map<string, string> => {
    if (rowsByPk.size === 0) {
      return new Map<string, string>();
    }
    return detectRefreshConflicts({ columns, rows: rowsByPk, pending });
  }, [columns, rowsByPk, pending]);

  // Closes the current keystroke-coalescing session so the next edit
  // starts a fresh undo step even if it targets the same cell within
  // the time window. Called from cell-input onBlur — when focus leaves
  // a cell, that visit is a logical boundary even if the user clicks
  // back in within the second.
  const endEditSession = useCallback((): void => historyDispatch({ type: "end-coalesce-session" }), []);
  const canUndo: boolean = historyState.past.length > 0;
  const canRedo: boolean = historyState.future.length > 0;
  const clearResolved = useCallback(
    (clientIds: string[]): void => {
      if (clientIds.length === 0) {
        return;
      }
      dispatch({ type: "clear-resolved", resolved: { clientIds } });
      clearSaveIssues(clientIds);
    },
    [clearSaveIssues]
  );

  // Count of local validation issues in the current pending set. Memoised so
  // consumers can branch UI on it (hide "Retry save", gate "Preview SQL")
  // without re-running the full builder on every render.
  const localValidationIssueCount: number = useMemo((): number => {
    if (!isDirtyChanges(pending)) {
      return 0;
    }
    return buildLocalMutationValidationIssues(columns, pending).length;
  }, [columns, pending]);

  // Shared gate for Save and Preview SQL: when local validation finds
  // issues, surface the same structured alert and per-cell highlights and
  // return false so the caller bails before building the wire payload.
  // Returns true when the batch is locally clean (server may still reject).
  const validateLocalEdits = useCallback((): boolean => {
    const localIssues: ICellValidationIssue[] = buildLocalMutationValidationIssues(columns, pending);
    if (localIssues.length === 0) {
      return true;
    }
    const insertIndices: Map<string, number> = insertIndexByClientId(pending);
    setSaveIssuesByClientId(saveIssuesFromLocalValidation(localIssues));
    setSaveError(
      createTableEditorAlert({
        tone: "error",
        operation: "save",
        title: "Fix invalid cells before saving",
        message: `The batch was not sent because ${localIssues.length} pending cell${localIssues.length === 1 ? " has" : "s have"} invalid values.`,
        details: localIssues
          .slice(0, MAX_LOCAL_VALIDATION_DETAILS)
          .map((issue: ICellValidationIssue): string => localIssueDetail(issue, insertIndices)),
        nextSteps: ["Correct the highlighted cells, then save again."],
        kind: "validation"
      })
    );
    return false;
  }, [columns, pending]);

  const saveAll = useCallback(async (): Promise<boolean> => {
    if (inFlightRef.current) {
      return false;
    }
    if (!isDirtyChanges(pending)) {
      return true;
    }
    if (primaryKeyColumns.length === 0) {
      setSaveError(
        createTableEditorAlert({
          tone: "warn",
          operation: "save",
          title: "Save unavailable",
          message: "This table has no primary key, so inline edits cannot be matched back to a single row.",
          nextSteps: ["Add a primary key to the table, then reopen Browse Data."]
        })
      );
      setSaveIssuesByClientId(new Map<string, string>());
      return false;
    }

    if (!validateLocalEdits()) {
      return false;
    }

    // Shared with the SQL preview path so both can't drift. Update keys
    // come from the serialized PK string (the reducer captures originals
    // when the cell is first edited, which is correct even when the row
    // has been paged off-screen since); insert payloads are the
    // dispatched row values.
    const request: IManagedDbBatchRequest = buildBatchRequestFromPending(tableName, tableSchema, pending, atomicSave);
    inFlightRef.current = true;
    setIsSaving(true);
    setSaveError(null);
    // Save-issue highlights are NOT wiped here. The success and
    // partial-failure branches below replace them with the new response's
    // issues; the catch path leaves the previous batch's highlights in
    // place so a network failure doesn't strip context the user might
    // still need.
    try {
      const response = await TableEditorService.BatchMutate(request);
      const payload: IManagedDbBatchResponse = response.data;
      // Surface alerts + issue highlights before refresh so the user
      // sees them immediately; doesn't matter whether refresh or
      // clear-resolved runs first for these.
      if (!payload.success) {
        setSaveIssuesByClientId(saveIssuesFromBatchResponse(payload));
        setSaveError(buildTableEditorSaveFailureAlert(request, payload));
      } else {
        setSaveError(null);
        setSaveIssuesByClientId(new Map<string, string>());
        setSavedAt(Date.now());
      }
      // CRITICAL: refresh BEFORE clearing pending. The pending overlay
      // is what's keeping the user-visible cell values stable during
      // the round-trip — clearing it first would unmask the OLD base
      // rows for a render frame before the refetched NEW rows land,
      // producing a brief "old value flashes back, then new value
      // appears" flicker. Refreshing first replaces the base with the
      // already-committed-server values; the overlay then peels off
      // onto an identical-value base, so the eye sees nothing change.
      await refreshRows();
      const results: IManagedDbBatchResult[] = payload.results ?? [];
      const resolvedIds: string[] = results
        .filter((r: IManagedDbBatchResult): boolean => r.success && r.clientId !== undefined)
        .map((r: IManagedDbBatchResult): string => r.clientId as string);
      if (resolvedIds.length > 0) {
        // Always clear the resolved (successful) mutations — applies on
        // full success AND on partial failure. Pre-fix the partial-
        // failure branch left every mutation dirty, so retrying Save
        // re-sent already-applied inserts and produced duplicate-key
        // errors.
        dispatch({ type: "clear-resolved", resolved: { clientIds: resolvedIds } });
        // Resolved mutations are persisted server-side. Undoing back into
        // them would resurrect rows the database has already committed
        // and confuse Save (re-sending the same inserts as duplicates).
        // Wipe history so the undo stack only covers post-save edits.
        historyDispatch({ type: "clear-history" });
      }
      return payload.success === true;
    } catch (error: unknown) {
      const alert: ITableEditorAlert = await buildTableEditorErrorAlert(error, {
        operation: "save",
        title: "Save failed",
        fallbackMessage: "The pending table changes could not be saved."
      });
      setSaveError(alert);
      // Intentionally do NOT wipe saveIssuesByClientId here. The previous
      // batch's per-row highlights are still meaningful — the rows
      // they tag haven't moved off the grid just because the next save
      // attempt threw before reaching the server.
      return false;
    } finally {
      inFlightRef.current = false;
      setIsSaving(false);
    }
  }, [pending, primaryKeyColumns, tableName, tableSchema, refreshRows, atomicSave, columns]);

  // Keyboard shortcuts. ⌘/Ctrl+S saves; Esc discards; ⌘/Ctrl+Z undoes
  // and ⌘/Ctrl+Shift+Z (or ⌘/Ctrl+Y) redoes. Save / discard gate on
  // persistable/visible draft state + not-already-saving so a stray Esc
  // doesn't blow away editor state during a network round-trip. Undo / redo are intentionally
  // skipped when the user is typing inside an editable element so the
  // browser's native input undo / redo isn't hijacked.
  useEffect((): (() => void) | undefined => {
    if (!shortcutsEnabled) {
      return undefined;
    }
    const handler = (e: KeyboardEvent): void => {
      const dirty: boolean = isDirtyChanges(pending);
      const cmd: boolean = e.metaKey || e.ctrlKey;
      const key: string = e.key.toLowerCase();
      if (cmd && key === "s" && dirty && !isSaving) {
        e.preventDefault();
        void saveAll();
        return;
      }
      // Esc-discard removed deliberately. The shortcut bypassed the
      // 3-edit confirm threshold AND wiped undo history, so an
      // accidental press could nuke arbitrary in-flight work with no
      // recovery. Discard now requires the explicit toolbar button,
      // which routes through the confirm popover for non-trivial edit
      // counts. Cmd+S to save is kept (saves are reversible — the row
      // is in the DB; Esc-discard wasn't).
      // Undo / redo. Skip when an editable element is focused — the user
      // expects the input's own history to handle backspace / restoration
      // there, not the table-wide stack.
      if (cmd && (key === "z" || key === "y")) {
        const target = e.target as HTMLElement | null;
        const editableFocused: boolean =
          target !== null &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.tagName === "SELECT" ||
            target.isContentEditable === true);
        if (editableFocused) {
          return;
        }
        const isRedo: boolean = key === "y" || (key === "z" && e.shiftKey);
        if (isRedo && canRedo) {
          e.preventDefault();
          redo();
        } else if (!isRedo && canUndo) {
          e.preventDefault();
          undo();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return (): void => window.removeEventListener("keydown", handler);
  }, [shortcutsEnabled, pending, isSaving, saveAll, discardAll, undo, redo, canUndo, canRedo]);

  const isDirty: boolean = isDirtyChanges(pending);
  const dirtyCount: number = pendingChangesMutationCount(pending);

  return {
    pending,
    isDirty,
    dirtyCount,
    isSaving,
    saveError,
    saveIssuesByClientId,
    savedAt,
    atomicSave,
    setAtomicSave,
    setSaveError,
    editCell,
    editInsertCell,
    addInsertRow,
    cloneRow,
    addRowFromValues,
    removeInsertRow,
    markRowDeleted,
    unmarkRowDeleted,
    discardAll,
    saveAll,
    validateLocalEdits,
    localValidationIssueCount,
    refreshConflictsByPk,
    clearResolved,
    undo,
    redo,
    canUndo,
    canRedo,
    endEditSession
  };
};
